import { GetStaticProps } from 'next';
import { GetStaticPropsContext } from 'next/types';
import { serverSideTranslations } from '@utils/i18n/serverSideTranslations';
import { useRouter } from 'next/router';
import { ENavTransparency } from '@features/navigation/typescript';
import getHomepageData from '@features/homepage/api';
import { TCmsBlocks } from '@features/cms/typescript';
import { getLayoutData } from '@features/layout/api';
import { ISeo, TLocale } from '@typescript/index';
import MainSeo from '@components/MainSeo/MainSeo';
import RenderCmsBlock from '@features/cms/components/RenderCmsBlock';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import { setCache } from '@utils/cacheHelpers';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import { PUBLIC_HOST, DEFAULT_LOCALE } from '@config/index';
import { trackRevalidation } from '@utils/revalidationTracker';
import logger from '@utils/logger';
import { observer } from 'mobx-react-lite';

export type PropsType = {
  homePageBlocks: Record<string, TCmsBlocks>;
  seo: ISeo | null;
  isBuildTime?: boolean;
};

const Home = observer(({ homePageBlocks, seo, isBuildTime }: PropsType) => {
  const router = useRouter();

  if (isBuildTime) {
    return <div>Build time</div>;
  }

  const currentPath = router.locale !== DEFAULT_LOCALE ? `${PUBLIC_HOST}/${router.locale}/` : `${PUBLIC_HOST}/`;

  return (
    <>
      <SelfCanonicalLink currentUrl={currentPath} />
      <main id="main-content">
        {seo && <MainSeo seo={seo} />}
        {Object.keys(homePageBlocks).map((blockKey, index) => (
          <RenderCmsBlock key={blockKey} block={homePageBlocks[blockKey]} isFirst={index === 0} />
        ))}
      </main>
    </>
  );
});

export const getStaticProps: GetStaticProps = async ({ locale }: GetStaticPropsContext) => {
  if (process.env.IS_BUILD) {
    return {
      props: {
        homePageBlocks: {},
        seo: null,
        isBuildTime: true,
        navParams: { navTransparency: ENavTransparency.BOTH_TRANSPARENT },
        ...(await serverSideTranslations(locale as string)),
      },
      revalidate: 1,
    };
  }

  const homepageData = await getHomepageData(locale as TLocale);

  // Handle notFound case
  if ('notFound' in homepageData && homepageData.notFound) {
    return {
      notFound: true,
      revalidate: homepageData.revalidate || 1,
    };
  }

  const { homePageBlocks, seo } = homepageData;

  // cache dalej działa normalnie
  setCache('seo', seo, 3600);

  try {
    await trackRevalidation('', locale as string, 900, 'static', 'index');
  } catch (error) {
    logger.error('Failed to track revalidation:', error);
  }

  return {
    props: {
      ...getDevProps('static', 900),
      homePageBlocks: homePageBlocks ?? {},
      seo: seo ?? null,
      isBuildTime: false,
      ...(await getLayoutData(locale as TLocale)),
      ...(await serverSideTranslations(locale as string)),
      navParams: { navTransparency: ENavTransparency.BOTH_TRANSPARENT },
    },
    revalidate: 900,
  };
};

export default Home;
